import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, MonitorSmartphone, ShieldCheck, Zap } from "lucide-react";
import { PageHero, Reveal, SectionHead, LineReveal } from "../components/Reveal";
import { CTABanner } from "../components/CTABanner";
import { ERP_MODULES, CONTACT, IMAGES } from "../data/content";

const BENEFITS = [
  { icon: Zap, title: "10x Faster Operations", desc: "Attendance, fees and reports in seconds, not hours." },
  { icon: ShieldCheck, title: "Secure & Centralized", desc: "Role-based access with every record in one place." },
  { icon: MonitorSmartphone, title: "Web + Mobile", desc: "Admin dashboard plus Android & iOS apps for parents and staff." },
];

export default function ERP() {
  const navigate = useNavigate();
  return (
    <main data-testid="erp-page">
      <section className="relative overflow-hidden bg-dg-navy bg-grid-dark pt-36 pb-24 sm:pt-44 sm:pb-28 noise">
        <div className="absolute -top-40 right-0 h-96 w-[36rem] rounded-full bg-dg-blue/30 blur-[140px]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-dg-cyan/10 blur-[110px]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-dg-cyan/30 bg-dg-cyan/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-dg-cyan">
              Flagship Solution
            </span>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
              <LineReveal delay={0.1}>SMART BUSINESS</LineReveal>
              <LineReveal delay={0.24}>MANAGEMENT.</LineReveal>
              <LineReveal delay={0.38}><span className="text-grad">ONE POWERFUL ERP.</span></LineReveal>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400"
            >
              Digitize your operations, simplify management and keep your business connected from one
              centralized platform — built for schools, institutions and growing businesses.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="mt-9 flex flex-col gap-4 sm:flex-row"
            >
              <button
                data-testid="erp-demo-btn"
                onClick={() => navigate("/contact?service=ERP%20%26%20Business%20Automation")}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-dg-cyan px-8 py-4 font-bold text-dg-navy transition-all duration-300 hover:bg-white hover:shadow-[0_0_35px_rgba(0,184,212,0.45)]"
              >
                Request ERP Demo <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                data-testid="erp-whatsapp-btn"
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 font-bold text-white transition-all duration-300 hover:border-dg-cyan hover:text-dg-cyan"
              >
                <MessageCircle size={17} /> WhatsApp Us
              </a>
            </motion.div>
          </div>
          <Reveal delay={0.3}>
            <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
              <img src={IMAGES.server} alt="Secure enterprise infrastructure behind DG Hubs School ERP" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-dg-navy via-dg-navy/30 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-dg-cyan">Live System</p>
                <div className="mt-2 flex items-end justify-between">
                  <p className="font-display text-2xl font-extrabold text-white">14 Modules</p>
                  <p className="text-xs text-slate-300">99.9% uptime</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-dg-light bg-grid-light py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead
            light
            eyebrow="ERP Modules"
            title="Every Department. Every Stakeholder. One Platform."
            sub="A complete modular system — start with what you need, add modules as you grow."
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {ERP_MODULES.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 0.06}>
                <div
                  data-testid={`erp-module-${i}`}
                  className="group h-full rounded-2xl border border-dg-blue/10 bg-white p-5 sm:p-6 shadow-lg shadow-blue-900/5 transition-all duration-500 hover:-translate-y-1.5 hover:border-dg-cyan/50 hover:shadow-xl"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-dg-light text-dg-blue transition-all duration-500 group-hover:bg-dg-navy group-hover:text-dg-cyan">
                    <m.icon size={19} />
                  </span>
                  <h3 className="font-display mt-4 text-sm sm:text-base font-bold text-dg-navy">{m.name}</h3>
                  <p className="mt-1.5 text-xs sm:text-[13px] text-slate-500">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-dg-navy2 py-24 sm:py-32 noise">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow="Why It Wins" title="Built for Institutions That Can't Afford Chaos" />
          <div className="grid gap-6 md:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.1}>
                <div data-testid={`erp-benefit-${i}`} className="h-full rounded-3xl border border-white/8 bg-white/[0.03] p-8 transition-all duration-500 hover:border-dg-cyan/40">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white"><b.icon size={21} /></span>
                  <h3 className="font-display mt-6 text-xl font-bold text-white">{b.title}</h3>
                  <p className="mt-2.5 text-sm text-slate-400">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner title="READY TO DIGITIZE YOUR INSTITUTION?" sub="Book a free live demo — we'll walk your team through every module on a call." />
    </main>
  );
}
